


import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import { Input, Button, message } from 'antd';
import {
    UserOutlined, LockOutlined, MailOutlined, GoogleOutlined,
    EyeInvisibleOutlined, EyeTwoTone, ArrowRightOutlined, ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import API from '../../API/API';
import { ROUTES } from '../../Routes/Routes';

/* ═══════════════════════════════════════════════════════════════
   CSS (unchanged)
═══════════════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,700;0,9..144,900;1,9..144,300;1,9..144,700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

:root{
  --navy:  #040D1E;
  --blue1: #0B3D91;
  --blue2: #1B5FCC;
  --blue3: #2E7CF6;
  --blue4: #5B9BFF;
  --blue5: #9DC3FF;
  --ice:   #E8F2FF;
  --white: #FFFFFF;
  --text:  #0C1A35;
  --text2: #2D4470;
  --muted: #7A93B8;
  --ibg:   #F2F7FF;
  --iborder: rgba(46,124,246,0.22);
  --ifocus:  #2E7CF6;
}

.z-root{
  font-family:'DM Sans',sans-serif;
  height:100vh;width:100vw;
  overflow:hidden;
  background:var(--navy);
  display:flex;
  position:relative;
  color:var(--white);
}

/* canvas layers */
.z-aurora{position:absolute;inset:0;pointer-events:none;z-index:0;}

.z-mesh{position:absolute;inset:0;pointer-events:none;overflow:hidden;z-index:0;}
.z-b{position:absolute;border-radius:50%;filter:blur(120px);}
.z-b1{width:900px;height:600px;background:radial-gradient(ellipse,rgba(11,61,145,0.7) 0%,transparent 70%);top:-250px;left:-200px;animation:mb1 14s ease-in-out infinite;}
.z-b2{width:700px;height:700px;background:radial-gradient(circle,rgba(46,124,246,0.4) 0%,transparent 65%);bottom:-300px;left:15%;animation:mb2 18s ease-in-out infinite;}
.z-b3{width:500px;height:500px;background:radial-gradient(circle,rgba(91,155,255,0.28) 0%,transparent 65%);top:5%;right:-80px;animation:mb3 12s ease-in-out 2s infinite;}
.z-b4{width:350px;height:350px;background:radial-gradient(circle,rgba(157,195,255,0.18) 0%,transparent 65%);top:55%;right:380px;animation:mb1 20s ease-in-out 5s infinite reverse;}
@keyframes mb1{0%,100%{transform:translate(0,0) scale(1)}40%{transform:translate(40px,-30px) scale(1.08)}70%{transform:translate(-20px,20px) scale(0.94)}}
@keyframes mb2{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-30px,-40px) scale(1.06)}}
@keyframes mb3{0%,100%{transform:translate(0,0)}60%{transform:translate(20px,30px) scale(1.1)}}

.z-noise{position:absolute;inset:0;pointer-events:none;z-index:1;opacity:0.025;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");background-size:200px 200px;}

.z-grid{position:absolute;inset:0;pointer-events:none;z-index:0;background-image:linear-gradient(rgba(255,255,255,0.022) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.022) 1px,transparent 1px);background-size:80px 80px;}

.z-orb{position:absolute;border-radius:50%;pointer-events:none;z-index:1;}
.z-o1{width:12px;height:12px;top:14%;left:38%;background:#5B9BFF;box-shadow:0 0 30px rgba(91,155,255,1),0 0 80px rgba(91,155,255,0.5);animation:fo 5s ease-in-out infinite;}
.z-o2{width:7px;height:7px;top:70%;left:20%;background:#9DC3FF;box-shadow:0 0 20px rgba(157,195,255,0.9);animation:fo 8s ease-in-out 2s infinite reverse;}
.z-o3{width:5px;height:5px;top:45%;left:52%;background:#E8F2FF;box-shadow:0 0 14px rgba(232,242,255,0.8);animation:fo 7s ease-in-out 1s infinite;}
.z-o4{width:9px;height:9px;top:28%;left:62%;background:#2E7CF6;box-shadow:0 0 24px rgba(46,124,246,0.9),0 0 60px rgba(46,124,246,0.4);animation:fo 9s ease-in-out 3.5s infinite;}
@keyframes fo{0%,100%{transform:translateY(0)}50%{transform:translateY(-28px)}}

/* ── Left panel ── */
.z-left{
  flex:1;position:relative;z-index:2;
  display:flex;flex-direction:column;justify-content:space-between;
  padding:3rem 4.5rem 3rem 4rem;overflow:hidden;
}
.z-left::after{
  content:'';position:absolute;top:0;right:-2px;bottom:0;width:160px;
  background:var(--navy);clip-path:polygon(100% 0,100% 100%,0 100%);z-index:4;
}

.z-topbar{display:flex;align-items:center;gap:14px;opacity:0;animation:su 0.7s 0.05s ease forwards;}
.z-mark{
  position:relative;width:46px;height:46px;border-radius:14px;
  background:linear-gradient(135deg,#2E7CF6,#0B3D91);
  display:flex;align-items:center;justify-content:center;
  font-family:'Fraunces',serif;font-size:1.5rem;font-weight:900;color:#fff;
  box-shadow:0 0 0 1px rgba(91,155,255,0.3),0 8px 32px rgba(11,61,145,0.6),0 0 60px rgba(46,124,246,0.35);
}
.z-mark::before{content:'';position:absolute;inset:-1px;border-radius:15px;background:linear-gradient(135deg,rgba(255,255,255,0.25),transparent);pointer-events:none;}
.z-brand{font-size:0.83rem;font-weight:600;letter-spacing:0.15em;text-transform:uppercase;color:rgba(255,255,255,0.45);}

.z-hero{flex:1;display:flex;flex-direction:column;justify-content:center;max-width:540px;}

.z-pill{display:inline-flex;align-items:center;gap:10px;font-size:0.66rem;letter-spacing:0.2em;text-transform:uppercase;color:var(--blue4);margin-bottom:1.8rem;opacity:0;animation:su 0.6s 0.2s ease forwards;}
.z-pill-dash{width:28px;height:1.5px;background:linear-gradient(90deg,var(--blue3),var(--blue5));}

.z-h1{font-family:'Fraunces',serif;font-size:clamp(3.2rem,5vw,5.8rem);font-weight:900;line-height:0.94;letter-spacing:-0.03em;margin-bottom:2rem;opacity:0;animation:su 0.8s 0.3s ease forwards;}
.z-h1 .solid{color:#fff;display:block;}
.z-h1 .outline{display:block;color:transparent;-webkit-text-stroke:2px var(--blue4);font-style:italic;font-weight:300;}
.z-h1 .accent{display:block;background:linear-gradient(90deg,#fff 0%,var(--blue4) 60%,var(--blue5) 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;font-weight:700;}

.z-desc{font-size:0.96rem;line-height:1.78;color:rgba(255,255,255,0.42);max-width:400px;margin-bottom:3rem;opacity:0;animation:su 0.7s 0.45s ease forwards;}

.z-features{display:flex;gap:10px;flex-wrap:wrap;opacity:0;animation:su 0.7s 0.58s ease forwards;}
.z-feat{font-size:0.69rem;letter-spacing:0.06em;padding:7px 16px;border-radius:100px;border:1px solid rgba(91,155,255,0.22);color:rgba(255,255,255,0.38);background:rgba(46,124,246,0.07);backdrop-filter:blur(8px);transition:all 0.2s;}
.z-feat:hover{border-color:rgba(91,155,255,0.5);color:rgba(255,255,255,0.7);background:rgba(46,124,246,0.14);}

.z-bottom{opacity:0;animation:su 0.6s 0.7s ease forwards;}
.z-divider{width:100%;height:1px;background:linear-gradient(90deg,rgba(255,255,255,0.08),transparent);margin-bottom:1.8rem;}
.z-stats{display:flex;}
.z-stat{padding-right:2.5rem;margin-right:2.5rem;border-right:1px solid rgba(255,255,255,0.08);}
.z-stat:last-child{border-right:none;padding-right:0;margin-right:0;}
.z-sn{font-family:'Fraunces',serif;font-size:2.2rem;font-weight:700;color:#fff;display:block;line-height:1;margin-bottom:5px;}
.z-sl{font-size:0.66rem;letter-spacing:0.1em;text-transform:uppercase;color:rgba(255,255,255,0.3);}

/* ── Right panel ── */
.z-right{
  width:520px;flex-shrink:0;
  position:relative;z-index:5;
  display:flex;align-items:center;justify-content:center;
  padding:2rem;
}

/*
  TILT FIX: The tilt transform is applied to .z-tilt-shell (a purely visual wrapper).
  .z-card is a separate non-transformed layer that holds all interactive content.
  This prevents mouse events from interrupting input focus.
*/
.z-tilt-outer{
  width:100%;max-width:420px;
  position:relative;
  perspective:1200px;
}
.z-tilt-shell{
  position:absolute;inset:0;
  border-radius:28px;
  background:var(--white);
  box-shadow:
    0 0 0 1px rgba(46,124,246,0.1),
    0 2px 4px rgba(11,61,145,0.06),
    0 12px 40px rgba(11,61,145,0.18),
    0 40px 100px rgba(7,20,40,0.55),
    0 0 80px rgba(46,124,246,0.08);
  pointer-events:none;
  will-change:transform;
  overflow:hidden;
}
/* shimmer bar on the shell */
.z-tilt-shell::before{
  content:'';
  position:absolute;top:0;left:0;right:0;height:4px;
  background:linear-gradient(90deg,#0B3D91,#2E7CF6,#9DC3FF,#2E7CF6,#0B3D91);
  background-size:200% 100%;
  animation:shimmerBar 4s linear infinite;
}
.z-tilt-shell::after{
  content:'';
  position:absolute;top:-80px;right:-80px;
  width:260px;height:260px;border-radius:50%;
  background:radial-gradient(circle,rgba(46,124,246,0.06) 0%,transparent 70%);
}
@keyframes shimmerBar{0%{background-position:0% 0%}100%{background-position:200% 0%}}

/* The actual interactive card — no transform, ever */
.z-card{
  position:relative;z-index:1;
  width:100%;
  background:transparent;
  border-radius:28px;
  padding:3rem 2.8rem 2.5rem;
  opacity:0;
  animation:cardIn 0.9s 0.1s cubic-bezier(0.16,1,0.3,1) forwards;
}

@keyframes cardIn{from{opacity:0;transform:translateY(40px) scale(0.96);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes su{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
@keyframes fi{from{opacity:0}to{opacity:1}}

.z-card-badge{display:inline-flex;align-items:center;gap:7px;font-size:0.6rem;letter-spacing:0.22em;text-transform:uppercase;color:var(--blue3);margin-bottom:1.1rem;}
.z-badge-dot{width:7px;height:7px;border-radius:50%;background:var(--blue3);box-shadow:0 0 8px rgba(46,124,246,0.7);animation:pulse 2.5s ease-in-out infinite;}
@keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(0.65);opacity:0.4}}

.z-card-title{font-family:'Fraunces',serif;font-size:2.5rem;font-weight:900;line-height:1.02;color:var(--text);margin-bottom:0.25rem;}
.z-card-title .light{font-weight:300;font-style:italic;color:var(--muted);font-size:1.9rem;display:block;}
.z-card-sub{font-size:0.85rem;color:var(--muted);margin-bottom:2.2rem;margin-top:0.4rem;line-height:1.5;}

/* Fields */
.z-field{margin-bottom:1.1rem;}
.z-flabel{display:block;font-size:0.6rem;letter-spacing:0.16em;text-transform:uppercase;color:var(--muted);margin-bottom:7px;padding-left:1px;font-weight:500;}

.z-card .ant-input,
.z-card .ant-input-affix-wrapper{
  background:var(--ibg) !important;
  border:1.5px solid var(--iborder) !important;
  border-radius:13px !important;
  color:var(--text) !important;
  padding:12px 16px !important;
  font-family:'DM Sans',sans-serif !important;
  font-size:0.9rem !important;
  font-weight:400 !important;
  transition:all 0.22s !important;
  box-shadow:0 1px 4px rgba(11,61,145,0.05),inset 0 1px 2px rgba(255,255,255,0.8) !important;
}
.z-card .ant-input-affix-wrapper:hover,
.z-card .ant-input:hover{
  border-color:rgba(46,124,246,0.45) !important;
  background:#fff !important;
  box-shadow:0 2px 8px rgba(11,61,145,0.08),inset 0 1px 2px rgba(255,255,255,0.8) !important;
}
.z-card .ant-input-affix-wrapper-focused,
.z-card .ant-input-affix-wrapper:focus-within{
  border-color:var(--ifocus) !important;
  background:#fff !important;
  box-shadow:0 0 0 4px rgba(46,124,246,0.12),0 2px 8px rgba(11,61,145,0.1) !important;
}
.z-card .ant-input::placeholder,
.z-card .ant-input-affix-wrapper input::placeholder{color:#B8CBE8 !important;font-size:0.87rem !important;}
.z-card .ant-input-affix-wrapper .ant-input{background:transparent !important;border:none !important;padding:0 !important;box-shadow:none !important;font-size:0.9rem !important;}
.z-card .anticon{color:#A0B8D8 !important;font-size:0.95rem !important;}

/* CTA */
.z-cta{
  width:100%;height:52px !important;
  border-radius:14px !important;
  background:linear-gradient(135deg,#0B3D91 0%,#2E7CF6 55%,#5B9BFF 100%) !important;
  background-size:200% 200% !important;
  border:none !important;color:#fff !important;
  font-family:'DM Sans',sans-serif !important;
  font-weight:600 !important;font-size:0.9rem !important;letter-spacing:0.08em !important;
  cursor:pointer;position:relative;overflow:hidden;
  transition:transform 0.18s,box-shadow 0.22s !important;
  box-shadow:0 4px 16px rgba(11,61,145,0.3),0 8px 32px rgba(46,124,246,0.25),inset 0 1px 0 rgba(255,255,255,0.2) !important;
  display:flex !important;align-items:center !important;justify-content:center !important;gap:9px !important;
}
.z-cta::before{content:'';position:absolute;inset:0;background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.22) 50%,transparent 100%);transform:translateX(-100%);transition:transform 0.6s ease;}
.z-cta:hover{transform:translateY(-2px) !important;box-shadow:0 8px 24px rgba(11,61,145,0.35),0 16px 48px rgba(46,124,246,0.3),inset 0 1px 0 rgba(255,255,255,0.25) !important;}
.z-cta:hover::before{transform:translateX(100%);}
.z-cta:active{transform:translateY(0) scale(0.99) !important;}

/* Google */
.z-google{
  width:100%;height:48px !important;
  border-radius:14px !important;background:#fff !important;
  border:1.5px solid rgba(46,124,246,0.18) !important;
  color:var(--text2) !important;
  font-family:'DM Sans',sans-serif !important;font-size:0.87rem !important;font-weight:500 !important;
  cursor:pointer;transition:all 0.2s !important;
  display:flex !important;align-items:center !important;justify-content:center !important;gap:10px !important;
  box-shadow:0 1px 4px rgba(11,61,145,0.07),inset 0 1px 0 rgba(255,255,255,1) !important;
}
.z-google:hover{border-color:rgba(46,124,246,0.4) !important;background:var(--ice) !important;box-shadow:0 4px 20px rgba(11,61,145,0.12) !important;transform:translateY(-2px) !important;color:var(--blue2) !important;}
.z-google .anticon{color:#5B9BFF !important;font-size:1.05rem !important;}

.z-or{display:flex;align-items:center;gap:12px;margin:1.5rem 0;color:#C5D5EC;font-size:0.68rem;letter-spacing:0.12em;text-transform:uppercase;}
.z-or::before,.z-or::after{content:'';flex:1;height:1px;background:#DDE8F7;}

.z-back{display:inline-flex;align-items:center;gap:7px;margin-top:1.3rem;font-size:0.73rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted) !important;cursor:pointer;text-decoration:none;transition:color 0.2s;font-weight:500;}
.z-back:hover{color:var(--blue3) !important;}

.z-footer{margin-top:2rem;padding-top:1.5rem;border-top:1px solid #EAF0FB;font-size:0.68rem;color:#B8CBE8;text-align:center;letter-spacing:0.04em;display:flex;align-items:center;justify-content:center;gap:8px;}
.z-footer-dot{width:3px;height:3px;border-radius:50%;background:#C8D8EF;}

.z-body{animation:fi 0.3s ease;}

@media(max-width:900px){
  .z-left{display:none;}
  .z-right{width:100%;background:radial-gradient(ellipse at 50% 0%,rgba(46,124,246,0.15) 0%,transparent 70%);}
}
`;

/* ═══════════════════════════════════════════════════════════════
   AURORA CANVAS (unchanged)
═══════════════════════════════════════════════════════════════ */
const AuroraCanvas = () => {
    const ref = useRef(null);
    useEffect(() => {
        const c = ref.current; if (!c) return;
        const ctx = c.getContext('2d');
        let W = c.width = window.innerWidth;
        let H = c.height = window.innerHeight;
        let t = 0;
        const stars = Array.from({ length: 120 }, () => ({
            x: Math.random() * W, y: Math.random() * H,
            r: Math.random() * 0.9 + 0.2,
            a: Math.random() * 0.6 + 0.1,
            tw: Math.random() * Math.PI * 2,
            spd: Math.random() * 0.02 + 0.005,
        }));
        const pts = Array.from({ length: 50 }, () => ({
            x: Math.random() * W, y: Math.random() * H,
            r: Math.random() * 1.3 + 0.3,
            vx: (Math.random() - 0.5) * 0.2,
            vy: (Math.random() - 0.5) * 0.2,
            a: Math.random() * 0.4 + 0.1,
        }));
        let raf;
        const draw = () => {
            ctx.clearRect(0, 0, W, H);
            t += 0.008;
            stars.forEach(s => {
                s.tw += s.spd;
                ctx.beginPath();
                ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(180,210,255,${s.a * (0.6 + 0.4 * Math.sin(s.tw))})`;
                ctx.fill();
            });
            const N = pts.length;
            for (let i = 0; i < N; i++) {
                const p = pts[i];
                p.x += p.vx; p.y += p.vy;
                if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
                if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(91,155,255,${p.a})`;
                ctx.fill();
                for (let j = i + 1; j < N; j++) {
                    const q = pts[j];
                    const dx = p.x - q.x, dy = p.y - q.y;
                    const d = Math.sqrt(dx * dx + dy * dy);
                    if (d < 120) {
                        ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y);
                        ctx.strokeStyle = `rgba(91,155,255,${0.08 * (1 - d / 120)})`;
                        ctx.lineWidth = 0.7; ctx.stroke();
                    }
                }
            }
            // Aurora wave
            const grad = ctx.createLinearGradient(0, 0, W, 0);
            grad.addColorStop(0, 'transparent');
            grad.addColorStop(0.2, `rgba(11,61,145,${0.12 + 0.05 * Math.sin(t)})`);
            grad.addColorStop(0.5, `rgba(46,124,246,${0.08 + 0.04 * Math.sin(t + 1)})`);
            grad.addColorStop(0.8, `rgba(91,155,255,${0.1 + 0.04 * Math.sin(t + 2)})`);
            grad.addColorStop(1, 'transparent');
            ctx.fillStyle = grad;
            const aH = 180 + 60 * Math.sin(t * 0.7);
            ctx.beginPath(); ctx.moveTo(0, 0);
            for (let x = 0; x <= W; x += 8) {
                ctx.lineTo(x, 40 * Math.sin((x / W) * Math.PI * 2 + t) + 30 * Math.sin((x / W) * Math.PI + t * 1.3) + aH);
            }
            ctx.lineTo(W, 0); ctx.closePath(); ctx.fill();
            raf = requestAnimationFrame(draw);
        };
        draw();
        const onResize = () => { W = c.width = window.innerWidth; H = c.height = window.innerHeight; };
        window.addEventListener('resize', onResize);
        return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
    }, []);
    return <canvas ref={ref} className="z-aurora" />;
};

/* ═══════════════════════════════════════════════════════════════
   TILT WRAPPER (unchanged)
═══════════════════════════════════════════════════════════════ */
const TiltCard = ({ children }) => {
    const shellRef = useRef(null);

    const onMove = useCallback((e) => {
        const shell = shellRef.current; if (!shell) return;
        const rect = shell.parentElement.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        shell.style.transform = `rotateY(${x * 7}deg) rotateX(${-y * 5}deg)`;
    }, []);

    const onLeave = useCallback(() => {
        const shell = shellRef.current; if (!shell) return;
        shell.style.transform = 'rotateY(0deg) rotateX(0deg)';
    }, []);

    return (
        <div className="z-tilt-outer" onMouseMove={onMove} onMouseLeave={onLeave}>
            <div ref={shellRef} className="z-tilt-shell" style={{ transition: 'transform 0.25s ease' }} />
            <div className="z-card">
                {children}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   SEPARATE FORM COMPONENTS (FIX FOR CURSOR DISAPPEARING)
═══════════════════════════════════════════════════════════════ */

// Field wrapper component
const Field = memo(({ label, children }) => (
    <div className="z-field">
        <span className="z-flabel">{label}</span>
        {children}
    </div>
));

// Login Form Component
const LoginForm = memo(({ 
    email, setEmail, 
    password, setPassword, 
    loading, handleLogin,
    googleLoading, handleGoogleSuccess 
}) => {
    return (
        <div className="z-body" key="login">
            <Field label="Username">
                <Input 
                    prefix={<UserOutlined />} 
                    placeholder="Enter your username" 
                    size="large"
                    value={email} 
                    onChange={e => setEmail(e.target.value)}
                    onPressEnter={handleLogin} 
                />
            </Field>
            <Field label="Password">
                <Input.Password 
                    prefix={<LockOutlined />} 
                    placeholder="Enter your password" 
                    size="large"
                    value={password} 
                    onChange={e => setPassword(e.target.value.replace(/\s/g, ''))}
                    iconRender={v => v ? <EyeTwoTone twoToneColor="#2E7CF6" /> : <EyeInvisibleOutlined />}
                    onPressEnter={handleLogin} 
                />
            </Field>
            <div style={{ marginTop: '1.7rem' }}>
                <Button className="z-cta" onClick={handleLogin} loading={loading}>
                    Sign In <ArrowRightOutlined />
                </Button>
            </div>
            <div className="z-or">or</div>
            <GoogleLogin 
                onSuccess={handleGoogleSuccess} 
                onError={() => message.error('Google login failed')}
                render={({ onClick }) => (
                    <Button className="z-google" icon={<GoogleOutlined />} onClick={onClick} loading={googleLoading}>
                        Continue with Google
                    </Button>
                )} 
            />
        </div>
    );
});

// Forgot Password Form Component
const ForgotPasswordForm = memo(({ 
    email, setEmail, 
    loading, handleForgotPassword, 
    setFormType 
}) => {
    return (
        <div className="z-body" key="forgot">
            <Field label="Email Address">
                <Input 
                    prefix={<MailOutlined />} 
                    placeholder="you@company.com" 
                    size="large"
                    value={email} 
                    onChange={e => setEmail(e.target.value)}
                    onPressEnter={handleForgotPassword} 
                />
            </Field>
            <div style={{ marginTop: '1.7rem' }}>
                <Button className="z-cta" onClick={handleForgotPassword} loading={loading}>
                    Send Reset Link <ArrowRightOutlined />
                </Button>
            </div>
            <a className="z-back" onClick={() => setFormType('login')}>
                <ArrowLeftOutlined style={{ fontSize: 11 }} /> Back to login
            </a>
        </div>
    );
});

// Reset Password Form Component
const ResetPasswordForm = memo(({ 
    newPassword, setNewPassword, 
    loading, handleResetPassword, 
    setFormType 
}) => {
    return (
        <div className="z-body" key="reset">
            <Field label="New Password">
                <Input.Password 
                    prefix={<LockOutlined />} 
                    placeholder="Create a strong password" 
                    size="large"
                    value={newPassword} 
                    onChange={e => setNewPassword(e.target.value)}
                    iconRender={v => v ? <EyeTwoTone twoToneColor="#2E7CF6" /> : <EyeInvisibleOutlined />}
                    onPressEnter={handleResetPassword} 
                />
            </Field>
            <div style={{ marginTop: '1.7rem' }}>
                <Button className="z-cta" onClick={handleResetPassword} loading={loading}>
                    Set New Password <ArrowRightOutlined />
                </Button>
            </div>
            <a className="z-back" onClick={() => setFormType('login')}>
                <ArrowLeftOutlined style={{ fontSize: 11 }} /> Back to login
            </a>
        </div>
    );
});

/* ═══════════════════════════════════════════════════════════════
   LOGIN COMPONENT (Main)
═══════════════════════════════════════════════════════════════ */
const Login = ({ onLogin }) => {
    const [formType, setFormType] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [resetToken, setResetToken] = useState('');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const token = searchParams.get('token');
        if (token) { 
            setResetToken(token); 
            setFormType('resetPassword'); 
        }
    }, [searchParams]);

    const updateUserStatus = async (id) => {
        try { await API.updateIsActive(id, true); } catch (e) { console.error(e); }
    };

    const handleLogin = async () => {
        if (!email || !password) return message.error('Please enter both username and password');
        setLoading(true);
        try {
            const r = await API.LoginUser({ userName: email.replace(/\s/g, '').toLowerCase(), password });
            if (r?.status) {
                if (r?.token) {
                    localStorage.setItem('token', r.token);
                    localStorage.setItem('userData', JSON.stringify({ ...r.user }));
                    await updateUserStatus(r?.user?.id);
                }
                if (r?.user?.firstLogin) { 
                    setFormType('resetPassword'); 
                    setLoading(false);
                    return; 
                }
                onLogin(); 
                navigate(ROUTES.HOME);
            } else { 
                message.error(r?.message || 'Invalid credentials'); 
            }
        } catch (e) { 
            message.error(e?.message || 'An error occurred.'); 
        } finally { 
            setLoading(false); 
        }
    };

    const handleForgotPassword = async () => {
        if (!email) return message.error('Please enter your email');
        setLoading(true);
        try {
            const r = await API.ForgotPassword({ email });
            if (r?.status) { 
                message.success('Reset link sent to your email'); 
                setFormType('resetPassword'); 
            } else {
                message.error(r?.message || 'Failed to send reset link');
            }
        } catch { 
            message.error('An error occurred.'); 
        } finally { 
            setLoading(false); 
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword) return message.error('Please enter a new password');
        const token = resetToken || localStorage.getItem('token');
        if (!token) return message.error('Invalid reset attempt');
        setLoading(true);
        try {
            const r = await API.ResetPassword({ token, newPassword });
            if (r?.status) {
                message.success('Password reset successful');
                setFormType('login'); 
                setPassword(''); 
                setNewPassword('');
                navigate(ROUTES.LOGIN);
            } else {
                message.error(r?.message || 'Reset failed');
            }
        } catch { 
            message.error('Error during password reset.'); 
        } finally { 
            setLoading(false); 
        }
    };

    const handleGoogleSuccess = async (cred) => {
        try {
            const token = cred.credential;
            const { email: gEmail } = jwtDecode(token);
            setGoogleLoading(true);
            const r = await API.GoogleLogin({ tokenId: token, email: gEmail });
            if (r?.status && r?.token) {
                localStorage.setItem('token', r.token);
                localStorage.setItem('userData', JSON.stringify(r.user));
                await updateUserStatus(r?.user?.id);
                onLogin(); 
                navigate(ROUTES.HOME);
            } else {
                message.error(r?.message || 'Google login failed!');
            }
        } catch { 
            message.error('Google login failed'); 
        } finally { 
            setGoogleLoading(false); 
        }
    };

    const TITLES = {
        login:          { a: 'Sign in', b: 'to continue.', sub: 'Welcome back to your workspace' },
        forgotPassword: { a: 'Reset', b: 'password.', sub: 'Enter your registered email' },
        resetPassword:  { a: 'New', b: 'password.', sub: 'Choose something secure' },
    };
    const T = TITLES[formType];

    const renderForm = () => {
        switch (formType) {
            case 'login':
                return (
                    <LoginForm
                        email={email}
                        setEmail={setEmail}
                        password={password}
                        setPassword={setPassword}
                        loading={loading}
                        handleLogin={handleLogin}
                        googleLoading={googleLoading}
                        handleGoogleSuccess={handleGoogleSuccess}
                    />
                );
            case 'forgotPassword':
                return (
                    <ForgotPasswordForm
                        email={email}
                        setEmail={setEmail}
                        loading={loading}
                        handleForgotPassword={handleForgotPassword}
                        setFormType={setFormType}
                    />
                );
            case 'resetPassword':
                return (
                    <ResetPasswordForm
                        newPassword={newPassword}
                        setNewPassword={setNewPassword}
                        loading={loading}
                        handleResetPassword={handleResetPassword}
                        setFormType={setFormType}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <style>{CSS}</style>
            <div className="z-root">
                <AuroraCanvas />
                <div className="z-mesh">
                    <div className="z-b z-b1" /><div className="z-b z-b2" />
                    <div className="z-b z-b3" /><div className="z-b z-b4" />
                </div>
                <div className="z-noise" />
                <div className="z-grid" />
                <div className="z-orb z-o1" /><div className="z-orb z-o2" />
                <div className="z-orb z-o3" /><div className="z-orb z-o4" />

                {/* LEFT */}
                <div className="z-left">
                    <div className="z-topbar">
                        <div className="z-mark">A</div>
                        <span className="z-brand">Autopulse CRM</span>
                    </div>
                    <div className="z-hero">
                        <div className="z-pill"><span className="z-pill-dash" />Sales Automation Platform</div>
                        <h1 className="z-h1">
                            <span className="solid">Close more.</span>
                            <span className="outline">Work less.</span>
                            <span className="accent">Grow faster.</span>
                        </h1>
                        <p className="z-desc">Autopulse replaces repetitive sales grunt work with intelligent automation — so your team spends every hour closing deals, not updating spreadsheets.</p>
                        <div className="z-features">
                            {['Pipeline Automation', 'Lead Scoring', 'Live Analytics', 'AI Forecasting', 'Smart Follow-ups'].map(f => (
                                <span key={f} className="z-feat">{f}</span>
                            ))}
                        </div>
                    </div>
                    <div className="z-bottom">
                        <div className="z-divider" />
                        <div className="z-stats">
                            {[['3.4×', 'Pipeline velocity'], ['60%', 'Less manual work'], ['99.9%', 'Uptime SLA']].map(([n, l]) => (
                                <div key={l} className="z-stat">
                                    <span className="z-sn">{n}</span>
                                    <span className="z-sl">{l}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* RIGHT */}
                <div className="z-right">
                    <TiltCard>
                        <div className="z-card-badge"><span className="z-badge-dot" />Secure Access</div>
                        <div className="z-card-title">{T.a}<span className="light">{T.b}</span></div>
                        <p className="z-card-sub">{T.sub}</p>
                        {renderForm()}
                        <div className="z-footer">
                            <span>© {new Date().getFullYear()} Autopulse CRM</span>
                            <span className="z-footer-dot" />
                            <span>All rights reserved</span>
                        </div>
                    </TiltCard>
                </div>
            </div>
        </>
    );
};

export default Login;